import { PropsWithChildren, useLayoutEffect, useRef } from "react";
import { MDXPagesLayout } from "./mdx-pages";
import { PageProps } from "gatsby";
import { TopPanel } from "../shared/components/layout/top-panel";
import { useIsLoadingFailed } from "../providers/loading-manager/useIsPageLoadingFailed";
import { LoadingFailed } from "../shared/components/banners/LoadingFailed";
import { TransitionCurtain } from "../shared/components/transition-curtain";
import { hideTopPanel } from "../shared/components/layout/top-panel/utils";
import { getPageType, isNewsPage } from "../shared/utils/getPageType";
import { LanguageProvider } from "../providers/language";
import { Frontmatter } from "../mdx-pages/types/fontmatter";

const isTransitionEnabled = (
  currentPathname: string,
  { pathname, state }: PageProps["location"],
) => {
  const targetPageType = getPageType(pathname);
  const currentPageType = getPageType(currentPathname);
  const byButtonLink = (state as any)?.type === "button-link";

  if (targetPageType === "internal" && currentPageType === "main") {
    return byButtonLink;
  }

  if (targetPageType === "modal" && currentPageType === "modal") {
    return true;
  }

  return targetPageType !== currentPageType;
};

const getTransitionDirection = (
  currentPathname: string,
  { pathname }: PageProps["location"],
) => {
  const currentPageType = getPageType(currentPathname);
  const targetPageType = getPageType(pathname);

  if (isNewsPage(currentPathname) && targetPageType === "modal") {
    return "up";
  }

  if (targetPageType === "main") {
    return "down";
  }

  if (currentPageType === "modal") {
    return targetPageType === "modal" ? "up" : "down";
  }

  return "up";
};

export const CommonLayout = ({
  props,
  children,
}: PropsWithChildren<{
  props: PageProps<object, { frontmatter?: Frontmatter }>;
}>) => {
  const { isLoadingFailed } = useIsLoadingFailed();
  const { location } = props;
  const { pathname, hash } = location;
  const pathnameRef = useRef(pathname);
  const pageType = getPageType(pathname);
  const isModalPage = pageType === "modal";
  const isMdxLayoutPage = !!props.pageContext.frontmatter;
  const transitionEnabled = isTransitionEnabled(pathnameRef.current, location);
  const transitionDirection = getTransitionDirection(
    pathnameRef.current,
    location,
  );
  const isMainPageTransition = pageType === "main" && hash === "#main";

  useLayoutEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  if (isLoadingFailed) {
    return <LoadingFailed />;
  }

  return (
    <>
      <LanguageProvider>
        <TopPanel isModalPage={isModalPage} />

        <TransitionCurtain
          pageKey={isModalPage ? pathname : pageType}
          isEnabled={transitionEnabled}
          // hide top panel on the curtain is closed when navigating to the main page from the others
          onClose={isMainPageTransition ? hideTopPanel : undefined}
          direction={transitionDirection}
        >
          {isMdxLayoutPage ? (
            <MDXPagesLayout props={props}>{children}</MDXPagesLayout>
          ) : (
            children
          )}
        </TransitionCurtain>
      </LanguageProvider>
    </>
  );
};
